import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Loader2, X } from "lucide-react";
import api, { imgUrl, formatPrice } from "../lib/api";
import { useLanguage } from "../context/LanguageContext";
import { catLabel, productName } from "../lib/i18n";

/**
 * Yazdıqca nəticə göstərən məhsul axtarışı.
 *
 * Əvvəl xanaya yazıb Enter basmaq lazım idi və müştəri /shop səhifəsinə
 * keçirdi — yəni nəticəni görmək üçün səhifə dəyişirdi. İndi ilk hərflərdən
 * sonra siyahı elə başlığın altında açılır.
 *
 * Server ucu `GET /api/products?search=` onsuz da var (ad, ingiliscə ad və
 * kod üzrə axtarır) — yeni uc əlavə edilmir.
 */

// İki hərfdən azı hər şeyə uyğun gəlir — nəticə faydasız olur.
const MIN_CHARS = 2;
// Hər hərfə sorğu getməsin. 300 ms yazı sürətinə uyğundur; ödəniş
// səhifəsindəki 500 ms endirim axtarışı üçündür, orada tələsmək lazım deyil.
const DEBOUNCE_MS = 300;
// Açılan siyahı qısa qalmalıdır — qalanı «Hamısına bax» ilə açılır.
const MAX_RESULTS = 6;

export const ProductSearch = ({ inline = false, autoFocus = false, onDone }) => {
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const boxRef = useRef(null);

  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [totalFound, setTotalFound] = useState(0);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const term = query.trim();
  const active = open && term.length >= MIN_CHARS;

  useEffect(() => {
    if (term.length < MIN_CHARS) {
      setResults([]);
      setTotalFound(0);
      setLoading(false);
      return;
    }
    // Cavablar sıradan çıxa bilər: «üzü» yazanda «üz» sorğusu sonra gəlsə
    // köhnə nəticə yenisinin üstünə yazılardı. `alive` bunun qarşısını alır.
    let alive = true;
    setLoading(true);
    const timer = setTimeout(() => {
      api.get("/products", { params: { search: term } })
        .then(({ data }) => {
          if (!alive) return;
          setTotalFound(data.length);
          setResults(data.slice(0, MAX_RESULTS));
        })
        .catch(() => {
          if (!alive) return;
          setTotalFound(0);
          setResults([]);
        })
        .finally(() => { if (alive) setLoading(false); });
    }, DEBOUNCE_MS);

    return () => { alive = false; clearTimeout(timer); };
  }, [term]);

  // Kənara toxunanda bağlanır.
  useEffect(() => {
    if (!active) return;
    const onDown = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
    };
  }, [active]);

  const close = useCallback(() => {
    setOpen(false);
    setQuery("");
    if (onDone) onDone();
  }, [onDone]);

  const goToProduct = (p) => {
    // Məhsul kodu ilə yox, uuid ilə keçirik: kodlar kateqoriya daxilində
    // təkrarlanır və `/product/<kod>` təsadüfi məhsul aça bilər.
    navigate(`/product/${p.id}`);
    close();
  };

  const goToAll = () => {
    if (!term) return;
    navigate(`/shop?search=${encodeURIComponent(term)}`);
    close();
  };

  const onSubmit = (e) => {
    e.preventDefault();
    goToAll();
  };

  const onKeyDown = (e) => {
    if (e.key === "Escape") {
      setOpen(false);
      e.currentTarget.blur();
    }
  };

  const panel = inline
    ? "mt-3 w-full"
    : "absolute right-0 top-full mt-3 w-[22rem] shadow-lg";

  return (
    <div ref={boxRef} className={inline ? "w-full" : "relative"}>
      <form
        onSubmit={onSubmit}
        className={`flex items-center border-b border-brand-line ${inline ? "pb-2" : ""}`}
      >
        <Search size={16} strokeWidth={1.5} className="text-brand-muted shrink-0" />
        <input
          data-testid="search-input"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder={t("search_placeholder")}
          aria-label={t("search_placeholder")}
          autoComplete="off"
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={autoFocus}
          className={`bg-transparent text-sm px-2 py-1 outline-none placeholder:text-brand-muted ${
            inline ? "w-full" : "w-36 focus:w-56 transition-[width] duration-300"
          }`}
        />
        {query && (
          <button
            type="button"
            onClick={close}
            aria-label={t("close")}
            data-testid="search-clear"
            className="text-brand-muted hover:text-brand-ink shrink-0"
          >
            <X size={14} strokeWidth={1.5} />
          </button>
        )}
      </form>

      {active && (
        <div
          data-testid="search-results"
          className={`${panel} bg-white border border-brand-line z-50 max-h-[70vh] overflow-y-auto`}
        >
          {loading && results.length === 0 && (
            <div className="flex items-center gap-2 px-4 py-6 text-sm text-brand-muted">
              <Loader2 size={15} strokeWidth={1.5} className="animate-spin" />
              {t("loading")}
            </div>
          )}

          {!loading && results.length === 0 && (
            <p className="px-4 py-6 text-sm text-brand-muted" data-testid="search-empty">
              {t("no_products")}
            </p>
          )}

          {results.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => goToProduct(p)}
              data-testid={`search-hit-${p.id}`}
              className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-brand-bg2 transition-colors border-b border-brand-line/60 last:border-b-0"
            >
              <div className="w-12 h-14 bg-brand-bg2 overflow-hidden shrink-0">
                <img
                  src={imgUrl(p.images && p.images[0])}
                  alt=""
                  loading="lazy"
                  className="w-full h-full object-cover"
                  onError={(e) => { e.target.src = imgUrl(null); }}
                />
              </div>
              <div className="min-w-0 flex-1">
                <p className="overline mb-0.5">{catLabel(p.category, lang)}</p>
                <p className="font-serif text-base text-brand-ink leading-tight truncate">
                  {productName(p, lang)}
                </p>
              </div>
              <span className="text-sm text-brand-sub whitespace-nowrap">
                {formatPrice(p.price)}
              </span>
            </button>
          ))}

          {/* Siyahı kəsilibsə müştəri qalanını görə bilməlidir. */}
          {totalFound > results.length && (
            <button
              type="button"
              onClick={goToAll}
              data-testid="search-view-all"
              className="w-full px-4 py-3 text-xs tracking-[0.15em] uppercase text-brand-ink hover:bg-brand-bg2 transition-colors border-t border-brand-line"
            >
              {t("view_all")} ({totalFound})
            </button>
          )}
        </div>
      )}
    </div>
  );
};
